import { useCallback, useState } from 'react';
import { formatCurrency } from '../helpers/formatters';
import { refundBooking } from '../../openapi/bookings/bookings';
import { useLang } from '../hooks/useLanguage';

function RefundChunksBlock({
    orders,
    reservationId,
    refundChunks,
    refundChunksError,
    reloadOrdersAndReservation,
    reservationHash,
    paymentMethods,
    venueEmail,
    venuePhone,
}: {
    orders: any[];
    reservationId: number;
    refundChunks: { name: string; max: number; method: string; note: string }[];
    refundChunksError: Error | null;
    reloadOrdersAndReservation: () => Promise<void>;
    reservationHash: string;
    paymentMethods: any[];
    venueEmail?: string | null;
    venuePhone?: string | null;
}) {
    const { getI18n, t } = useLang();

    const [rawRefundInputs, setRawRefundInputs] = useState<string[]>([]);
    const refundInputs = rawRefundInputs.map((value) => {
        const parsedValue = parseFloat(value);
        return isNaN(parsedValue) ? 0 : Math.max(0, parsedValue);
    });
    // Math.round (not raw * 100) because IEEE-754 makes 1.81 * 100 =
    // 180.99999999999997, which would never equal the integer
    // refundingMaxAmount (181 cents) and left the Refund button
    // permanently disabled on residual-cent refunds like a $1.81
    // leftover after a multi-card split.
    const totalRefundAmount = refundInputs.reduce(
        (acc, val) => acc + (val ? Math.round(val * 100) : 0),
        0,
    );

    // Let's handle the refund input problems
    const refundInputParseErrors = rawRefundInputs.map((value, idx) => {
        const parsedValue = parseFloat(value);
        if (isNaN(parsedValue)) {
            return t('errors.invalidAmount');
        }
        if (refundChunks[idx] === undefined) {
            return null; // no chunk for this index, skip validation
        }
        if (refundChunks[idx].max / 100 < parsedValue) {
            return t('errors.maxRefundExceeded', {
                max: formatCurrency(refundChunks[idx].max / 100),
            });
        }
        return null;
    });

    // Calculate the maximum amount that can be refunded
    // orders with status 'refunding' are considered for refunding, from there the amount needs to be decresed on transactions
    // refunding amounts are in the minus (so does the transactions)
    let refundingMaxAmount = 0;
    const refundingOrders: { id: number; amount: number }[] = [];
    orders.forEach((order) => {
        if (String(order.status) !== 'refunding') {
            return;
        }
        refundingMaxAmount += order.amount * -1; // totalAmount is negative for refunding orders
        order.transactions.forEach((trans) => {
            refundingMaxAmount += trans.amount;
        });
        refundingOrders.push({ id: order.id, amount: order.amount });
    });

    const handleRefundBooking = useCallback(async () => {
        try {
            await refundBooking(
                reservationId,
                {
                    orders: refundingOrders,
                    chunks: refundChunks.map((chunk, idx) => {
                        // isFinite guards against NaN at chunk indices the
                        // guest didn't touch (parseFloat on undefined →
                        // NaN), which would otherwise serialize to null and
                        // break the API's integer schema. Math.round (not
                        // floor) fixes the IEEE-754 drift where 1.81 * 100
                        // = 180.99… so floor would submit 180 cents
                        // instead of 181 on residual refunds.
                        const inputAmount = refundInputs[idx];
                        const amount = Number.isFinite(inputAmount)
                            ? Math.round(inputAmount * 100)
                            : 0;
                        return {
                            name: chunk.name,
                            amount,
                            method: chunk.method,
                            info: (chunk as any).info ?? null,
                        };
                    }),
                },
                { reservationHash },
            );
            // Reset inputs after a successful refund: a stale rawRefundInputs
            // entry at an index whose chunk has since been fully refunded (and
            // thus removed from the chunks list on reload) stays in the reduce
            // sum and makes totalRefundAmount permanently exceed
            // refundingMaxAmount on the next round, leaving the Refund button
            // disabled even when the visible input matches the new total.
            setRawRefundInputs([]);
            reloadOrdersAndReservation();
        } catch (error) {
            console.error('Error refund booking:', error);
            alert(t('errors.refundReservationError'));
            return;
        }
    }, [
        t,
        reservationId,
        reservationHash,
        refundingOrders,
        refundChunks,
        refundInputs,
        reloadOrdersAndReservation,
    ]);

    // Guest cannot refund "partially" - total amount must equal refunding max amount
    const isRefundSumMismatch = totalRefundAmount !== refundingMaxAmount;

    // Additional validation: guests must distribute ALL refund amount
    const hasPartialRefund = totalRefundAmount > 0 && totalRefundAmount < refundingMaxAmount;

    // Coupon refunds are agent-only (per-coupon legal/marketing
    // constraints). The server already rejects coupon chunks from the
    // widget path, but without this guard the guest saw a full refund
    // form with the coupon row disabled and no clear explanation of
    // why the Refund button never activated. Surface the self-service
    // block as its own state with a contact-the-venue message instead.
    const hasCouponChunk = refundChunks.some((c) => c.method === 'coupon' && (c.max || 0) > 0);

    if (refundingMaxAmount > 0 && hasCouponChunk) {
        const contactHref = venueEmail
            ? `mailto:${venueEmail}`
            : venuePhone
              ? `tel:${venuePhone.replace(/\s+/g, '')}`
              : null;
        return (
            <div className="fireknife-refund-chunks">
                {refundChunksError && <div>{refundChunksError.message}</div>}
                <h2 className="fireknife-refund-chunks__title">
                    {t('widgetReservationInfo.refundChunks.title')}
                </h2>
                <p className="fireknife-refund-chunks__info">
                    {t('widgetReservationInfo.refundChunks.couponContactAgent')}
                </p>
                {contactHref && (
                    <a
                        className="fireknife-refund-chunks__btn fireknife-btn fireknife-btn--primary"
                        href={contactHref}
                    >
                        {t('widgetReservationInfo.refundChunks.contactVenueButton')}
                    </a>
                )}
            </div>
        );
    }

    return (
        // only show refund chunks if there are any to refund
        refundingMaxAmount > 0 && (
            <div className="fireknife-refund-chunks">
                {refundChunksError && <div>{refundChunksError.message}</div>}
                <h2 className="fireknife-refund-chunks__title">
                    {t('widgetReservationInfo.refundChunks.title')}
                    {refundingMaxAmount > 0 && ` (${formatCurrency(refundingMaxAmount / 100)})`}
                </h2>
                {refundingMaxAmount > 0 && (
                    <p className="fireknife-refund-chunks__info">
                        {t('widgetReservationInfo.refundChunks.fullRefundRequired', {
                            amount: formatCurrency(refundingMaxAmount / 100),
                        })}
                    </p>
                )}
                <table className="fireknife-refund-chunks__table">
                    <thead>
                        <tr>
                            <th className="fireknife-text-start">
                                {t('widgetReservationInfo.refundChunks.name')}
                            </th>
                            <th className="fireknife-text-start">
                                {t('widgetReservationInfo.refundChunks.max')}
                            </th>
                            <th className="fireknife-text-start">
                                {t('widgetReservationInfo.refundChunks.method')}
                            </th>
                            <th>{t('widgetReservationInfo.refundChunks.refundAmount')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {refundChunks?.map((chunk, idx) => (
                            <tr key={chunk.name + idx}>
                                <td
                                    className="fireknife-text-start"
                                    aria-label={t('widgetReservationInfo.refundChunks.name')}
                                >
                                    {chunk.name}
                                </td>
                                <td
                                    className="fireknife-text-start"
                                    aria-label={t('widgetReservationInfo.refundChunks.max')}
                                >
                                    {formatCurrency(chunk.max / 100)}
                                </td>
                                <td
                                    className="fireknife-text-start"
                                    aria-label={t('widgetReservationInfo.refundChunks.method')}
                                >
                                    {paymentMethods.find((pm) => pm.slug === chunk.method) !==
                                    undefined
                                        ? getI18n(
                                              paymentMethods.find((pm) => pm.slug === chunk.method)
                                                  .name,
                                              paymentMethods.find((pm) => pm.slug === chunk.method)
                                                  .nameI18n,
                                          )
                                        : chunk.method}
                                </td>
                                <td className="fireknife-refund-chunks__input-td">
                                    <div className="fireknife-refund-chunks__form-box">
                                        <div
                                            className={`fireknife-form-control-box ${refundInputParseErrors[idx] ? 'fireknife-form-control-box--error' : ''}`}
                                        >
                                            <div className="fireknife-refund-chunks__amount-prefix">
                                                $
                                            </div>
                                            <input
                                                className="fireknife-refund-chunks__amount fireknife-form-control"
                                                type="number"
                                                inputMode="numeric"
                                                min={0}
                                                max={chunk.max / 100}
                                                // Coupon chunks are virtual —
                                                // no real money moves, and
                                                // coupon refunds are agent-only
                                                // (rule 3). The server rejects
                                                // coupon chunks from the widget
                                                // path; disable the input so the
                                                // guest can't even try.
                                                disabled={chunk.method === 'coupon'}
                                                readOnly={chunk.method === 'coupon'}
                                                value={rawRefundInputs[idx] || ''}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    setRawRefundInputs((prev) => {
                                                        const newInputs = [...prev];
                                                        newInputs[idx] = value;
                                                        return newInputs;
                                                    });
                                                }}
                                                style={{ width: 130, marginRight: 8 }}
                                            />
                                            {refundInputParseErrors[idx] && (
                                                <div className="fireknife-refund-chunks__error">
                                                    {refundInputParseErrors[idx]}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <button
                    className="fireknife-refund-chunks__btn fireknife-btn fireknife-btn--primary"
                    onClick={handleRefundBooking}
                    disabled={
                        isRefundSumMismatch ||
                        hasPartialRefund ||
                        refundInputParseErrors.some((err) => err !== null)
                    }
                >
                    {t('widgetReservationInfo.refundChunks.refundButton')}
                </button>
            </div>
        )
    );
}

export default RefundChunksBlock;
